import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { 
  Trash2, 
  GripVertical, 
  ImagePlus, 
  Upload, 
  Link as LinkIcon, 
  Globe, 
  Loader2, 
  AlertCircle, 
  CheckCircle2, 
  Search,
  Plus,
  Hotel,
  Camera,
  AlertTriangle,
  Settings,
  Check,
  ArrowLeft
} from 'lucide-react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { cn } from '@/lib/utils';
import { useToast } from '@/hooks/use-toast';
import { useGoogleApi } from '@/hooks/useGoogleApi';
import { storageService } from '@/lib/supabase/storage';
import { getUnsplashApiKey, getGoogleCustomSearchApiKey } from '@/lib/api-keys';
import {
  type BookingStayParamsInput,
  appendBookingStayParams,
  formatStayDateRange,
  getBookingHotelBaseUrl,
  getBookingStayParamsForTagEnrichment,
  isBookingHotelUrl,
  mergeRoomTagsByImageId,
  prepareBookingUrlForScrape,
  resolveBookingStayParams,
} from '@/lib/booking-scrape-url';

interface ImageManagerModalProps {
  isOpen: boolean;
  onClose: () => void;
  images: string[];
  onImagesChange: (images: string[]) => void;
  contextId?: string;
  contextType?: 'day' | 'activity' | 'customSection' | 'trip' | 'quotation' | 'accommodation' | 'flight' | 'tour' | 'transportation';
  /** Check-in/out da hospedagem ou cotação — usados ao chamar a API de imagens do Booking. */
  bookingStayParams?: BookingStayParamsInput;
}
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
// URL da API de scraping de imagens do Booking.com
// Em desenvolvimento, usa o proxy do Vite para evitar problemas de CORS
// Em produção, pode usar diretamente a API ou configurar um proxy no servidor
const SCRAPING_API_BASE_URL = import.meta.env.DEV 
  ? '/scrape-api' 
  : 'https://scrape-production-75f7.up.railway.app';
const SCRAPING_API_TIMEOUT = 45000; // doc da API recomenda ≥45s
interface ImageUrl {
  url: string;
  isValid: boolean;
  isLoading: boolean;
  error?: string;
}

interface ExtractedImage {
  url: string;
  tag?: string | null;
}

const mergeExtractedImages = (prev: ExtractedImage[], next: ExtractedImage[]): ExtractedImage[] => {
  const map = new Map<string, ExtractedImage>();
  for (const item of [...next, ...prev]) {
    if (!item.url) continue;
    const existing = map.get(item.url);
    if (!existing) {
      map.set(item.url, item);
    } else if (!existing.tag && item.tag) {
      map.set(item.url, { url: item.url, tag: item.tag });
    }
  }
  return Array.from(map.values());
};
export function ImageManagerModal({ 
  isOpen, 
  onClose, 
  images, 
  onImagesChange,
  contextId,
  contextType,
  bookingStayParams,
}: ImageManagerModalProps) {
  const { toast } = useToast();
  // Define a aba inicial com base na existência de imagens
  const initialTab = images.length > 0 ? "view" : "add";
  const [activeTab, setActiveTab] = React.useState<string>(initialTab);
  const [activeInnerTab, setActiveInnerTab] = React.useState<string>("url");
  const [selectedImages, setSelectedImages] = React.useState<Set<number>>(new Set());
  
  // Estados para a funcionalidade de upload
  const [files, setFiles] = React.useState<File[]>([]);
  const [imageUrls, setImageUrls] = React.useState<ImageUrl[]>([]);
  const [searchInput, setSearchInput] = React.useState('');
  const [unsplashInput, setUnsplashInput] = React.useState('');
  const [searchResults, setSearchResults] = React.useState<Array<{ title: string, link: string, image: { contextLink: string } }>>([]);
  const [unsplashResults, setUnsplashResults] = React.useState<Array<{ id: string, description: string, urls: { regular: string, small: string }, user: { name: string, links: { html: string } } }>>([]);
  const [extractedImages, setExtractedImages] = React.useState<ExtractedImage[]>([]);
  const [selectedExtractedImages, setSelectedExtractedImages] = React.useState<Set<number>>(new Set());
  const [selectedUnsplashImages, setSelectedUnsplashImages] = React.useState<Set<number>>(new Set());
  const [extractionSource, setExtractionSource] = React.useState<string>('');
  const [tagEnrichmentNote, setTagEnrichmentNote] = React.useState<string | null>(null);
  const [isDragging, setIsDragging] = React.useState(false);
  const [isUploading, setIsUploading] = React.useState(false);
  const [isExtracting, setIsExtracting] = React.useState(false);
  const [isSearching, setIsSearching] = React.useState(false);
  const [isUnsplashSearching, setIsUnsplashSearching] = React.useState(false);
  const [uploadProgress, setUploadProgress] = React.useState<{ [key: string]: number }>({});
  const [errors, setErrors] = React.useState<{ [key: string]: string }>({});
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const urlValidationAbortRef = React.useRef<AbortController | null>(null);
  const urlValidationGenRef = React.useRef(0);
  
  // Estado para controle de sessão da extensão
  const [currentSessionId, setCurrentSessionId] = React.useState<string | null>(null);
  
  // Função para gerar um ID de sessão único
  const generateSessionId = () => {
    return Date.now().toString() + '-' + Math.random().toString(36).substring(2, 9);
  };
  
  // Função para gerar nome único para arquivo
  const generateUniqueFileName = (originalName: string) => {
    const ext = originalName.split('.').pop();
    return `${Date.now()}-${Math.random().toString(36).substring(2, 9)}.${ext}`;
  };

  const prepareBookingUrl = React.useCallback(
    (url: string) => prepareBookingUrlForScrape(url, bookingStayParams),
    [bookingStayParams]
  );
  
  // Listener para mensagens do script do Booking (usado quando URL é detectada automaticamente)
  // A extensão é usada quando:
  // 1. Uma URL do Booking.com é detectada automaticamente no input
  // A API é usada quando:
  // 1. Um resultado do Booking.com é selecionado da busca (botão "Booking.com" foi clicado)
  React.useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      // Só processa mensagens se o modal estiver aberto
      if (!isOpen) return;
      
      console.log('Mensagem recebida no Image Manager:', event.data);
      
      // Verifica se a mensagem é da extensão
      const isValidSource = event.data.source === 'TRAVEL_MAGIC_EXTENSION';
      
      if (isValidSource) {
        console.log('Processando mensagem da extensão');
        
        // Processa mensagens de teste da extensão
        if (event.data.type === 'TEST_RESULT' && event.data.data?.allUrls) {
          setIsExtracting(false);
          
          const parsed: ExtractedImage[] = event.data.data.allUrls
            .map((item: { url?: string; tag?: string | null } | string) => {
              if (typeof item === 'string') {
                return { url: item, tag: null };
              }
              if (item?.url) {
                return { url: item.url, tag: item.tag?.trim() || null };
              }
              return null;
            })
            .filter((item): item is ExtractedImage => item !== null && item.url !== '');
          
          if (parsed.length > 0) {
            // Limpa os resultados de busca para focar nos resultados da extração
            setSearchResults([]);
            
            setExtractedImages(prev => mergeExtractedImages(prev, parsed));
            
            toast({
              title: 'Extração Concluída',
              description: `Foram extraídas ${parsed.length} imagens únicas.`,
            });
          } else {
            toast({
              title: 'Nenhuma imagem encontrada',
              description: 'Não foi possível encontrar imagens nesta página.',
              variant: 'destructive'
            });
          }
        } else {
          console.log('Formato de mensagem não reconhecido ou sem URLs');
        }
      } else {
        console.log('Mensagem ignorada: fonte inválida');
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [currentSessionId, toast, isOpen]);
  // Gerenciamento de imagens
  const handleRemoveImage = async (index: number) => {
    const imageToRemove = images[index];
    const newImages = [...images];
    newImages.splice(index, 1);
    // Apenas remove do array local. A deleção do storage deve ser feita após verificação de uso cruzado (backend ou serviço utilitário).
    onImagesChange(newImages);
    toast({
      title: "Imagem removida",
      description: "A imagem foi removida da galeria local. A exclusão do storage deve ser feita de forma segura no backend.",
      duration: 3000,
    });
  };
  const handleClearAll = async () => {
    if (window.confirm('Tem certeza que deseja remover todas as imagens?')) {
      setIsUploading(true);
      try {
        // Apenas limpa o array local. A deleção do storage deve ser feita após verificação de uso cruzado (backend ou serviço utilitário).
        onImagesChange([]);
        toast({
          title: "Todas as imagens removidas",
          description: "As imagens foram removidas da galeria local. A exclusão do storage deve ser feita de forma segura no backend.",
          duration: 3000,
        });
      } catch (error) {
        console.error('Erro ao remover todas as imagens:', error);
        toast({
          title: "Erro ao remover imagens",
          description: "Ocorreu um erro ao remover algumas imagens.",
          variant: "destructive",
          duration: 3000,
        });
      } finally {
        setIsUploading(false);
      }
    }
  };
  const handleRemoveSelected = async () => {
    if (selectedImages.size === 0) return;
    setIsUploading(true);
    try {
      // Apenas remove do array local. A deleção do storage deve ser feita após verificação de uso cruzado (backend ou serviço utilitário).
      const newImages = images.filter((_, index) => !selectedImages.has(index));
      onImagesChange(newImages);
      setSelectedImages(new Set());
      toast({
        title: "Imagens removidas",
        description: `${selectedImages.size} imagens foram removidas da galeria local. A exclusão do storage deve ser feita de forma segura no backend.`,
        duration: 3000,
      });
    } catch (error) {
      console.error('Erro ao remover imagens:', error);
      toast({
        title: "Erro ao remover imagens",
        description: "Ocorreu um erro ao remover algumas imagens.",
        variant: "destructive",
        duration: 3000,
      });
    } finally {
      setIsUploading(false);
    }
  };
  const handleDragEnd = (result: any) => {
    if (!result.destination) return;
    const items = Array.from(images);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);
    onImagesChange(items);
    
    // Limpa qualquer seleção após o reordenamento
    setSelectedImages(new Set());
  };
  const toggleImageSelection = (index: number) => {
    const newSelection = new Set(selectedImages);
    if (newSelection.has(index)) {
      newSelection.delete(index);
    } else {
      newSelection.add(index);
    }
    setSelectedImages(newSelection);
  };
  const selectAll = () => {
    if (selectedImages.size === images.length) {
      setSelectedImages(new Set());
    } else {
      const allIndices = new Set(images.map((_, index) => index));
      setSelectedImages(allIndices);
    }
  };
  // Funcionalidades de upload
  const validateFile = (file: File): string | null => {
    if (!ALLOWED_TYPES.includes(file.type)) {
      return `Tipo de arquivo não suportado: ${file.type}`;
    }
    if (file.size > MAX_FILE_SIZE) {
      return `Arquivo muito grande: ${(file.size / 1024 / 1024).toFixed(2)}MB (máximo: 5MB)`;
    }
    return null;
  };
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };
  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const droppedFiles = Array.from(e.dataTransfer.files);
      const validFiles: File[] = [];
      const errorMessages: { [key: string]: string } = {};
      
      droppedFiles.forEach(file => {
        const error = validateFile(file);
        if (error) {
          errorMessages[file.name] = error;
        } else {
          validFiles.push(file);
        }
      });
      
      if (Object.keys(errorMessages).length > 0) {
        setErrors(prev => ({ ...prev, ...errorMessages }));
      }
      
      if (validFiles.length > 0) {
        setFiles(prev => [...validFiles, ...prev]);
      }
    }
  };
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const selectedFiles = Array.from(e.target.files);
      const validFiles: File[] = [];
      const errorMessages: { [key: string]: string } = {};
      
      selectedFiles.forEach(file => {
        const error = validateFile(file);
        if (error) {
          errorMessages[file.name] = error;
        } else {
          validFiles.push(file);
        }
      });
      
      if (Object.keys(errorMessages).length > 0) {
        setErrors(prev => ({ ...prev, ...errorMessages }));
      }
      
      if (validFiles.length > 0) {
        setFiles(prev => [...validFiles, ...prev]);
      }
    }
    
    // Reset the input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };
  const loadImageElement = (src: string): Promise<{ isValid: boolean; error?: string }> => {
    return new Promise((resolve) => {
      const img = new Image();
      let settled = false;

      const finish = (result: { isValid: boolean; error?: string }) => {
        if (settled) return;
        settled = true;
        window.clearTimeout(timeoutId);
        img.onload = null;
        img.onerror = null;
        resolve(result);
      };

      const timeoutId = window.setTimeout(() => {
        finish({ isValid: false, error: 'Tempo esgotado ao carregar a imagem' });
      }, 15000);

      const confirmDimensions = () => {
        if (img.naturalWidth > 0 && img.naturalHeight > 0) {
          finish({ isValid: true });
          return;
        }
        finish({ isValid: false, error: 'URL não é uma imagem válida' });
      };

      img.referrerPolicy = 'no-referrer';
      img.onload = () => {
        if (typeof img.decode === 'function') {
          img.decode().then(confirmDimensions).catch(confirmDimensions);
          return;
        }
        confirmDimensions();
      };
      img.onerror = () => {
        finish({ isValid: false, error: 'Não foi possível carregar a imagem' });
      };
      img.src = src;
    });
  };

  const validateImageUrl = async (url: string): Promise<{ isValid: boolean; error?: string; aborted?: boolean }> => {
    const trimmed = url.trim();
    if (!trimmed) return { isValid: false, error: 'URL vazia' };

    const generation = ++urlValidationGenRef.current;
    urlValidationAbortRef.current?.abort();
    const abort = new AbortController();
    urlValidationAbortRef.current = abort;

    const isStale = () => generation !== urlValidationGenRef.current;

    try {
      const response = await fetch(trimmed, { signal: abort.signal });
      if (isStale()) return { isValid: false, aborted: true };

      if (response.ok) {
        const blob = await response.blob();
        if (isStale()) return { isValid: false, aborted: true };
        if (blob.size === 0) {
          return { isValid: false, error: 'URL não é uma imagem válida' };
        }
        const objectUrl = URL.createObjectURL(blob);
        try {
          return await loadImageElement(objectUrl);
        } finally {
          URL.revokeObjectURL(objectUrl);
        }
      }
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') {
        return { isValid: false, aborted: true };
      }
    }

    if (isStale()) return { isValid: false, aborted: true };
    return loadImageElement(trimmed);
  };
  const handleUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const url = e.target.value;
    const trimmed = url.trim();
    // Atualiza também o searchInput quando o usuário digita uma URL
    setSearchInput(url);
    
    if (trimmed && isValidUrl(trimmed)) {
      setImageUrls([{ url: trimmed, isValid: false, isLoading: true }]);
      
      validateImageUrl(trimmed).then(result => {
        if (result.aborted) return;
        setImageUrls(prev => {
          if (prev[0]?.url !== trimmed) return prev;
          return [{
            url: trimmed,
            isValid: result.isValid,
            isLoading: false,
            error: result.error
          }];
        });
      });
    } else {
      setImageUrls(url ? [{ url, isValid: false, isLoading: false, error: 'URL inválida' }] : []);
    }
  };
  const handleCancelUrl = () => {
    setImageUrls([]);
    setSearchInput(''); // Limpa também o searchInput
  };
  const handleAddUrl = async () => {
    if (!imageUrls.length) return;
    
    const urlToAdd = imageUrls[0]?.url;
    if (!urlToAdd) return;

    setIsUploading(true);

    try {
      const validUrl = isValidUrl(urlToAdd);
      if (!validUrl) {
        throw new Error('URL inválida');
      }
      await storageService.initStorage();
      
      // Usar smart upload strategy para URLs externas - preservar URLs sem upload
      const preservedUrl = await storageService.smartUpload('external', urlToAdd, `${contextType}`);
      onImagesChange([...images, preservedUrl]);
      setSearchInput('');
      setImageUrls([]);
      toast({
        title: "Imagem adicionada",
        description: "A URL externa foi preservada e adicionada à galeria.",
        duration: 3000,
      });
      setActiveTab("gallery");
    } catch (error) {
      console.error('Erro ao adicionar URL:', error);
      setImageUrls(prev => prev.map(item => 
        item.url === urlToAdd
          ? { ...item, isValid: false, isLoading: false, error: 'URL inválida ou inacessível' }
          : item
      ));
      toast({
        title: "Erro ao adicionar URL",
        description: "Não foi possível carregar a imagem a partir desta URL.",
        variant: "destructive",
        duration: 3000,
      });
    } finally {
      setIsUploading(false);
    }
  };
  const handleRemoveFile = (index: number) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };
  const isValidUrl = (string: string): boolean => {
    try {
      const newUrl = new URL(string);
      return newUrl.protocol === 'http:' || newUrl.protocol === 'https:';
    } catch (error) {
      return false;
    }
  };
  const handleGoogleSearch = async (mode: 'booking' | 'general' = 'general') => {
    if (!searchInput.trim()) return;
    
    setIsSearching(true);
    setSearchResults([]);
    
    // Clear any existing extraction results
    setExtractedImages([]);
    setSelectedExtractedImages(new Set());
    
    try {
      const apiKey = getGoogleCustomSearchApiKey();
      
      if (!apiKey) {
        throw new Error('Chave de API do Google não configurada. Configure-a na página de administração.');
      }
      
      // Usando os IDs corretos do mecanismo de pesquisa personalizada
      const searchEngineId = mode === 'booking' ? 'd13cfc7376f1c4a3c' : 'c380a2b220ccf47e1';
      
      // Construindo a URL com todos os parâmetros necessários
      const searchUrl = new URL('https://www.googleapis.com/customsearch/v1');
      searchUrl.searchParams.append('key', apiKey);
      searchUrl.searchParams.append('cx', searchEngineId);
      searchUrl.searchParams.append('q', searchInput);
      searchUrl.searchParams.append('searchType', 'image');
      searchUrl.searchParams.append('num', '10');
      searchUrl.searchParams.append('gl', 'br');
      searchUrl.searchParams.append('safe', 'active');
      
      console.log('URL de busca:', searchUrl.toString().replace(apiKey, 'API_KEY_HIDDEN'));
      
      const response = await fetch(searchUrl.toString());
      
      console.log('Status da resposta:', response.status);
      
      const data = await response.json();
      
      if (data.error) {
        console.error('Erro retornado pela API:', data.error);
        if (data.error.code === 403) {
          throw new Error('Chave da API inválida ou sem permissões necessárias. Verifique as configurações.');
        } else if (data.error.code === 429) {
          throw new Error('Limite de requisições excedido. Tente novamente mais tarde.');
        } else {
          throw new Error(data.error.message || 'Erro retornado pela API do Google');
        }
      }
      
      if (data.items && data.items.length > 0) {
        console.log('Resultados encontrados:', data.items.length);
        
        // Filtra resultados duplicados do Booking.com
        let filteredResults = data.items;
        
        if (mode === 'booking') {
          // Usa um Set para rastrear URLs únicas
          const uniqueUrls = new Set<string>();
          filteredResults = data.items.filter((item: { image?: { contextLink?: string } }) => {
            const contextLink = item.image?.contextLink;
            if (!contextLink) return false;
            
            // Se a URL já existe, ignora este resultado
            if (uniqueUrls.has(contextLink)) {
              return false;
            }
            
            // Adiciona a URL ao Set e mantém este resultado
            uniqueUrls.add(contextLink);
            return true;
          });
        }
        
        setSearchResults(filteredResults);
        
        // Garante que estamos na aba de busca para ver os resultados
        if (activeTab !== "add") {
          setActiveTab("add");
        }
        
        toast({
          title: "Busca concluída",
          description: `Foram encontrados ${filteredResults.length} resultados únicos.`,
          duration: 3000,
        });
      } else {
        console.log('Nenhum resultado encontrado');
        toast({
          title: "Nenhum resultado",
          description: "Nenhuma imagem encontrada para esta busca.",
          variant: "destructive",
          duration: 3000,
        });
      }
    } catch (error) {
      console.error('Erro na busca:', error);
      toast({
        title: "Erro na busca",
        description: error instanceof Error ? error.message : 'Erro desconhecido',
        variant: "destructive",
        duration: 3000,
      });
    } finally {
      setIsSearching(false);
    }
  };
  const handleSelectSearchResult = (result: { link: string, image?: { contextLink: string }, title?: string }) => {
    // Se for um resultado do Booking.com, inicia a extração usando a API
    // (quando vem do botão "Booking.com" na busca, usa API)
    if (result.image?.contextLink && result.image.contextLink.includes('booking.com')) {
      setExtractionSource(prepareBookingUrl(result.image.contextLink));
      setSearchResults([]);
      extractFromUrlWithAPI(result.image.contextLink);
    } else {
      // Adiciona a imagem diretamente
      const newImages = [result.link, ...images];
      onImagesChange(newImages);
      
      toast({
        title: "Imagem adicionada",
        description: "A imagem foi adicionada com sucesso.",
        duration: 3000,
      });
    }
  };
  // Função para extrair imagens de uma URL usando a EXTENSÃO (usada quando URL é detectada automaticamente)
  const extractFromUrlWithExtension = (url: string) => {
    if (!isValidUrl(url)) {
      toast({
        title: "URL inválida",
        description: "Por favor, insira uma URL válida",
        variant: "destructive",
        duration: 3000,
      });
      return;
    }
    
    setIsExtracting(true);
    
    try {
      // Gera um novo ID de sessão
      const sessionId = generateSessionId();
      setCurrentSessionId(sessionId);
      
      const bookingUrl = isBookingHotelUrl(url) ? prepareBookingUrl(url) : url;
      const urlObj = new URL(bookingUrl);
      urlObj.searchParams.append('sessionId', sessionId);
      
      window.open(urlObj.toString(), '_blank');
      
      // Foca na janela principal após um breve delay
      let attempts = 0;
      const focusInterval = setInterval(() => {
        window.focus();
        attempts++;
        if (attempts >= 5) clearInterval(focusInterval);
      }, 500);
      
      setTimeout(() => clearInterval(focusInterval), 3000);
      
      toast({
        title: "Extração iniciada",
        description: "A página foi aberta. A extensão irá extrair as imagens automaticamente.",
        duration: 3000,
      });
    } catch (error) {
      console.error('Erro ao iniciar extração:', error);
      setIsExtracting(false);
      toast({
        title: "Erro na extração",
        description: "Não foi possível abrir a página. Verifique se a URL é válida.",
        variant: "destructive",
        duration: 5000,
      });
    }
  };
  // Função para extrair imagens de uma URL usando a API de scraping (usada quando botão Booking.com é clicado)
  const extractFromUrlWithAPI = async (url: string) => {
    if (!isValidUrl(url)) {
      toast({
        title: "URL inválida",
        description: "Por favor, insira uma URL válida",
        variant: "destructive",
        duration: 3000,
      });
      return;
    }
    
    setIsExtracting(true);
    setTagEnrichmentNote(null);
    
    try {
      const sessionId = generateSessionId();
      setCurrentSessionId(sessionId);

      const scrapeTargetUrl = prepareBookingUrl(url);
      if (scrapeTargetUrl !== url) {
        console.log('[Image Manager] URL Booking com datas de estadia:', scrapeTargetUrl);
      }
      
      let apiUrl: string;
      if (import.meta.env.DEV) {
        const searchParams = new URLSearchParams();
        searchParams.append('url', scrapeTargetUrl);
        searchParams.append('sessionId', sessionId);
        apiUrl = `${SCRAPING_API_BASE_URL}/scrape-images?${searchParams.toString()}`;
      } else {
        const urlObj = new URL(`${SCRAPING_API_BASE_URL}/scrape-images`);
        urlObj.searchParams.append('url', scrapeTargetUrl);
        urlObj.searchParams.append('sessionId', sessionId);
        apiUrl = urlObj.toString();
      }
      
      toast({
        title: "Extração iniciada",
        description: "A extração de imagens foi iniciada. Isso pode levar alguns segundos.",
        duration: 3000,
      });
      
      // Cria um AbortController para timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), SCRAPING_API_TIMEOUT);
      
      // Chama a API de scraping
      const response = await fetch(apiUrl, {
        method: 'GET',
        signal: controller.signal,
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      clearTimeout(timeoutId);
      
      // Processa a resposta JSON
      const data = await response.json();
      
      // Verifica se a resposta foi bem-sucedida
      if (!response.ok) {
        throw new Error(data.message || `Erro na API: ${response.status} ${response.statusText}`);
      }
      
      // Verifica se a resposta é do formato esperado
      if (data.source === 'scraping-backend' && data.type === 'SCRAPED_IMAGES' && data.data?.allUrls) {
        // Função para normalizar URLs do Booking.com
        // Corrige dois problemas comuns:
        // 1. Unicode escapes (\u0026 → &)
        // 2. Falta de parâmetro de autenticação (&o= → &o=&hp=1)
        const normalizeBookingUrl = (url: string): string => {
          if (!url || typeof url !== 'string') {
            return url;
          }
          
          try {
            // 1. Decodificar unicode escapes (\u0026 → &)
            let normalized = url.replace(/\\u0026/g, '&').replace(/\u0026/g, '&');
            
            // 2. Apenas processar URLs do Booking.com (bstatic.com)
            if (!normalized.includes('bstatic.com')) {
              return normalized;
            }
            
            // 3. Adicionar parâmetro de autenticação &hp=1 se necessário
            if (!normalized.includes('&hp=1') && !normalized.includes('?hp=1')) {
              if (normalized.endsWith('&o=') || normalized.endsWith('o=')) {
                // Se termina com &o=, adiciona &hp=1
                normalized = normalized + '&hp=1';
              } else if (normalized.includes('?k=') || normalized.includes('&k=')) {
                // Se tem ?k= mas não tem &o=, verifica se precisa adicionar
                if (normalized.includes('&o=')) {
                  // Se tem &o= mas não tem &hp=1, adiciona
                  normalized = normalized.replace(/&o=$/, '&o=&hp=1');
                  if (!normalized.includes('&hp=1')) {
                    normalized = normalized + '&hp=1';
                  }
                } else {
                  // Se não tem &o=, adiciona &o=&hp=1
                  const separator = normalized.includes('?') ? '&' : '?';
                  normalized = normalized + separator + 'o=&hp=1';
                }
              }
            }
            
            return normalized;
          } catch (error) {
            console.error('[Image Manager] Erro ao normalizar URL:', url, error);
            return url; // Retorna URL original se houver erro
          }
        };
        
        const parsed: ExtractedImage[] = data.data.allUrls
          .map((item: { url?: string; tag?: string | null } | string) => {
            let url: string | null = null;
            let tag: string | null = null;

            if (typeof item === 'string') {
              url = item;
            } else if (item && typeof item === 'object') {
              url = item.url || null;
              tag = item.tag?.trim() || null;
            }

            if (!url) return null;

            return {
              url: normalizeBookingUrl(url),
              tag,
            };
          })
          .filter((item): item is ExtractedImage => item !== null && item.url !== '');
        
        console.log(`[Image Manager] Total de URLs recebidas da API: ${data.data.allUrls?.length || 0}`);
        console.log(`[Image Manager] Total de URLs processadas: ${parsed.length}`);

        let finalParsed = parsed;
        let taggedCount = finalParsed.filter((i) => i.tag).length;
        let enrichmentNote: string | null = null;
        console.log(`[Image Manager] Imagens com tag de quarto (1ª passagem): ${taggedCount}`);

        if (taggedCount === 0 && isBookingHotelUrl(scrapeTargetUrl)) {
          const enrichmentParams = getBookingStayParamsForTagEnrichment();
          const enrichmentUrl = appendBookingStayParams(
            getBookingHotelBaseUrl(scrapeTargetUrl),
            enrichmentParams
          );

          if (enrichmentUrl !== scrapeTargetUrl) {
            console.log('[Image Manager] 2ª passagem para tags com datas:', enrichmentUrl);

            const enrichmentSessionId = `${sessionId}-tags`;
            let enrichmentApiUrl: string;
            if (import.meta.env.DEV) {
              const enrichmentSearchParams = new URLSearchParams();
              enrichmentSearchParams.append('url', enrichmentUrl);
              enrichmentSearchParams.append('sessionId', enrichmentSessionId);
              enrichmentApiUrl = `${SCRAPING_API_BASE_URL}/scrape-images?${enrichmentSearchParams.toString()}`;
            } else {
              const enrichmentUrlObj = new URL(`${SCRAPING_API_BASE_URL}/scrape-images`);
              enrichmentUrlObj.searchParams.append('url', enrichmentUrl);
              enrichmentUrlObj.searchParams.append('sessionId', enrichmentSessionId);
              enrichmentApiUrl = enrichmentUrlObj.toString();
            }

            const enrichmentController = new AbortController();
            const enrichmentTimeoutId = setTimeout(
              () => enrichmentController.abort(),
              SCRAPING_API_TIMEOUT
            );

            try {
              const enrichmentResponse = await fetch(enrichmentApiUrl, {
                method: 'GET',
                signal: enrichmentController.signal,
                headers: { 'Content-Type': 'application/json' },
              });
              clearTimeout(enrichmentTimeoutId);

              const enrichmentData = await enrichmentResponse.json();
              if (
                enrichmentResponse.ok &&
                enrichmentData.source === 'scraping-backend' &&
                enrichmentData.type === 'SCRAPED_IMAGES' &&
                enrichmentData.data?.allUrls
              ) {
                const enrichmentParsed: ExtractedImage[] = enrichmentData.data.allUrls
                  .map((item: { url?: string; tag?: string | null } | string) => {
                    let imageUrl: string | null = null;
                    let tag: string | null = null;
                    if (typeof item === 'string') {
                      imageUrl = item;
                    } else if (item && typeof item === 'object') {
                      imageUrl = item.url || null;
                      tag = item.tag?.trim() || null;
                    }
                    if (!imageUrl) return null;
                    return {
                      url: normalizeBookingUrl(imageUrl),
                      tag,
                    };
                  })
                  .filter((item): item is ExtractedImage => item !== null && item.url !== '');

                const { images, mergedCount } = mergeRoomTagsByImageId(
                  finalParsed,
                  enrichmentParsed
                );
                finalParsed = images;
                taggedCount = mergedCount;

                if (mergedCount > 0) {
                  const requestedStay = resolveBookingStayParams(bookingStayParams);
                  enrichmentNote = `Tipos de quarto identificados com datas alternativas (${formatStayDateRange(enrichmentParams)}). O Booking não associa fotos às datas ${formatStayDateRange(requestedStay)} neste hotel.`;
                  console.log(
                    `[Image Manager] Tags enriquecidas via 2ª passagem: ${mergedCount} imagens`
                  );
                }
              }
            } catch (enrichmentError) {
              clearTimeout(enrichmentTimeoutId);
              console.warn('[Image Manager] Falha na 2ª passagem de tags:', enrichmentError);
            }
          }
        }
        
        if (finalParsed.length > 0) {
          setSearchResults([]);
          setTagEnrichmentNote(enrichmentNote);
          
          setExtractedImages(prev => mergeExtractedImages(prev, finalParsed));
          
          toast({
            title: 'Extração Concluída',
            description: taggedCount > 0
              ? enrichmentNote
                ? `${finalParsed.length} imagens (${taggedCount} com tipo de quarto, via datas alternativas).`
                : `${finalParsed.length} imagens (${taggedCount} com tipo de quarto).`
              : `${finalParsed.length} imagens. O Booking não expôs fotos por quarto para estas datas.`,
            duration: 3000,
          });
        } else {
          toast({
            title: 'Nenhuma imagem encontrada',
            description: 'Não foi possível encontrar imagens nesta página.',
            variant: 'destructive',
            duration: 3000,
          });
        }
      } else {
        console.error('[Image Manager] Formato de resposta inesperado:', data);
        throw new Error('Formato de resposta inesperado da API');
      }
    } catch (error: any) {
      console.error('Erro ao extrair imagens:', error);
      
      // Tratamento de erros específicos
      let errorMessage = 'Não foi possível processar a URL fornecida.';
      
      if (error.name === 'AbortError') {
        errorMessage = 'A requisição excedeu o tempo limite. Tente novamente.';
      } else if (error.message) {
        errorMessage = error.message;
      } else if (error instanceof TypeError && error.message.includes('fetch')) {
        errorMessage = 'Erro de conexão com a API. Verifique sua conexão com a internet.';
      }
      
      toast({
        title: "Erro na extração",
        description: errorMessage,
        variant: "destructive",
        duration: 5000,
      });
    } finally {
      setIsExtracting(false);
    }
  };
  // Função para extração do Booking.com quando o BOTÃO é clicado (usa API)
  const extractFromBooking = () => {
    if (!isValidUrl(searchInput)) {
      toast({
        title: "URL inválida",
        description: "Por favor, insira uma URL válida do Booking.com",
        variant: "destructive",
        duration: 3000,
      });
      return;
    }
    
    const prepared = prepareBookingUrl(searchInput);
    setExtractionSource(prepared);
    extractFromUrlWithAPI(searchInput);
    
    // Não fecha mais o modal durante a extração para que o usuário possa ver os resultados
    // onClose();
  };
  // Função para alternar a seleção de uma imagem extraída
  const toggleExtractedImageSelection = (index: number) => {
    const newSelection = new Set(selectedExtractedImages);
    if (newSelection.has(index)) {
      newSelection.delete(index);
    } else {
      newSelection.add(index);
    }
    setSelectedExtractedImages(newSelection);
  };
  // Função para selecionar todas as imagens extraídas
  const selectAllExtractedImages = () => {
    if (selectedExtractedImages.size === extractedImages.length) {
      setSelectedExtractedImages(new Set());
    } else {
      const allIndices = new Set(extractedImages.map((_, index) => index));
      setSelectedExtractedImages(allIndices);
    }
  };
  // Função para adicionar as imagens extraídas selecionadas
  const addSelectedExtractedImages = async () => {
    if (selectedExtractedImages.size === 0) {
      toast({
        title: "Nenhuma imagem selecionada",
        description: "Selecione pelo menos uma imagem para adicionar.",
        variant: "destructive",
        duration: 3000,
      });
      return;
    }
    setIsUploading(true);
    const uploadedUrls: string[] = [];
    try {
      await storageService.initStorage();
      // ✅ CORREÇÃO: URLs externas devem ser preservadas, não migradas para o Supabase
      // Apenas uploads diretos do usuário devem ir para o storage
      for (const index of Array.from(selectedExtractedImages)) {
        try {
          const imageUrl = extractedImages[index].url;
          console.log('🔗 Adding external image URL (preserving original):', imageUrl);
          // Usar estratégia inteligente: preservar URLs externas
          const finalUrl = await storageService.smartUpload('external', imageUrl);
          uploadedUrls.push(finalUrl);
        } catch (error) {
          console.error(`Erro ao processar imagem ${index}:`, error);
        }
      }
      if (uploadedUrls.length > 0) {
        onImagesChange([...images, ...uploadedUrls]);
        setExtractedImages([]);
        setSelectedExtractedImages(new Set());
        setExtractionSource('');
        setActiveTab("view");
        toast({
          title: "Imagens adicionadas",
          description: `${uploadedUrls.length} imagens foram adicionadas (URLs preservadas).`,
          duration: 3000,
        });
      }
    } catch (error) {
      console.error('Erro ao adicionar imagens:', error);
      toast({
        title: "Erro ao adicionar imagens",
        description: "Ocorreu um erro ao adicionar algumas imagens.",
        variant: "destructive",
        duration: 3000,
      });
    } finally {
      setIsUploading(false);
    }
  };
  const handleSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchInput(e.target.value);
    
    // Clear extracted images when user starts a new search
    if (extractedImages.length > 0) {
      setExtractedImages([]);
      setSelectedExtractedImages(new Set());
      setTagEnrichmentNote(null);
    }
  };
  const handleSubmitFiles = async () => {
    if (files.length === 0) return;
    setIsUploading(true);
    const uploadedUrls: string[] = [];
    const newErrors: { [key: string]: string } = {};
    const newProgress: { [key: string]: number } = { ...uploadProgress };
    try {
      await storageService.initStorage();
      // Novo padrão: pasta baseada apenas no tipo de contexto
      const folder = `${contextType}`;
      for (const file of files) {
        try {
          // Gerar nome único para o arquivo
          const uniqueName = generateUniqueFileName(file.name);
          // Fazer upload do arquivo
          const url = await storageService.smartUpload('user-upload', file, `${contextType}`);
          uploadedUrls.push(url);
          newProgress[file.name] = 100;
        } catch (error) {
          console.error(`Erro no upload do arquivo ${file.name}:`, error);
          newErrors[file.name] = 'Falha no upload. Tente novamente.';
        }
      }
      setErrors(prev => ({ ...prev, ...newErrors }));
      setUploadProgress(newProgress);
      if (uploadedUrls.length > 0) {
        onImagesChange([...images, ...uploadedUrls]);
        setFiles([]);
        toast({
          title: "Upload concluído",
          description: `${uploadedUrls.length} imagens adicionadas com sucesso.`,
          duration: 3000,
        });
      }
    } catch (error) {
      toast({
        title: "Erro no upload",
        description: "Ocorreu um erro ao processar as imagens.",
        variant: "destructive",
        duration: 3000,
      });
    } finally {
      setIsUploading(false);
    }
  };
  const handleUnsplashInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setUnsplashInput(e.target.value);
  };
  const handleUnsplashSearch = async () => {
    if (!unsplashInput.trim()) return;
    
    setIsUnsplashSearching(true);
    setUnsplashResults([]);
    
    try {
      // Obter a chave de API do Unsplash
      const accessKey = getUnsplashApiKey();
      
      if (!accessKey) {
        toast({
          title: "API não configurada",
          description: "Chave de API do Unsplash não configurada.",
          variant: "destructive",
          duration: 3000,
        });
        throw new Error('Chave de API do Unsplash não configurada. Configure-a na página de administração.');
      }
      
      const response = await fetch(`https://api.unsplash.com/search/photos?query=${encodeURIComponent(unsplashInput)}&per_page=30`, {
        headers: {
          'Authorization': `Client-ID ${accessKey}`
        }
      });
      
      if (!response.ok) {
        if (response.status === 403) {
          throw new Error('Chave de API inválida ou sem permissões necessárias.');
        } else if (response.status === 429) {
          throw new Error('Limite de requisições excedido. Tente novamente mais tarde.');
        } else {
          const errorData = await response.json();
          throw new Error(errorData.errors?.[0] || `Erro na API do Unsplash: ${response.status}`);
        }
      }
      
      const data = await response.json();
      
      if (data.results && data.results.length > 0) {
        setUnsplashResults(data.results);
        toast({
          title: "Busca concluída",
          description: `Foram encontrados ${data.results.length} resultados no Unsplash.`,
          duration: 3000,
        });
      } else {
        toast({
          title: "Nenhum resultado",
          description: "Nenhuma imagem encontrada no Unsplash para esta busca.",
          variant: "destructive",
          duration: 3000,
        });
      }
    } catch (error) {
      console.error('Erro na busca do Unsplash:', error);
      toast({
        title: "Erro na busca",
        description: error instanceof Error ? error.message : 'Erro desconhecido',
        variant: "destructive",
        duration: 3000,
      });
    } finally {
      setIsUnsplashSearching(false);
    }
  };
  const toggleUnsplashImageSelection = (index: number) => {
    const newSelection = new Set(selectedUnsplashImages);
    if (newSelection.has(index)) {
      newSelection.delete(index);
    } else {
      newSelection.add(index);
    }
    setSelectedUnsplashImages(newSelection);
  };
  const selectAllUnsplashImages = () => {
    if (selectedUnsplashImages.size === unsplashResults.length) {
      setSelectedUnsplashImages(new Set());
    } else {
      const allIndices = new Set(unsplashResults.map((_, index) => index));
      setSelectedUnsplashImages(allIndices);
    }
  };
  const addSelectedUnsplashImages = async () => {
    if (selectedUnsplashImages.size === 0) {
      toast({
        title: "Nenhuma imagem selecionada",
        description: "Selecione pelo menos uma imagem para adicionar.",
        variant: "destructive",
        duration: 3000,
      });
      return;
    }
    
    setIsUploading(true);
    const uploadedUrls: string[] = [];
    
    try {
      await storageService.initStorage();
      
      // Filtra as imagens selecionadas
      const selectedImages = Array.from(selectedUnsplashImages).map(index => unsplashResults[index]);
      
      // ✅ CORREÇÃO: Preservar URLs do Unsplash em vez de fazer upload
      for (const photo of selectedImages) {
        try {
          console.log('🔗 Adding Unsplash image URL (preserving original):', photo.urls.regular);
          // Usar estratégia inteligente: preservar URLs externas
          const finalUrl = await storageService.smartUpload('external', photo.urls.regular);
          uploadedUrls.push(finalUrl);
          
          // Salvar atribuição do Unsplash conforme exigido pelos termos de uso
          try {
            const attributionsKey = `unsplash_attributions_${contextType}_${contextId}`;
            const existingAttributions = JSON.parse(localStorage.getItem(attributionsKey) || '[]');
            const newAttribution = {
              url: finalUrl,
              originalUrl: photo.urls.regular,
              attribution: `Photo by ${photo.user.name} on Unsplash`,
              attributionUrl: `${photo.user.links.html}?utm_source=travel_magic&utm_medium=referral`
            };
            localStorage.setItem(attributionsKey, JSON.stringify([...existingAttributions, newAttribution]));
          } catch (error) {
            console.error('Erro ao salvar atribuições:', error);
          }
        } catch (error) {
          console.error('Erro ao processar imagem do Unsplash:', error);
        }
      }
      
      if (uploadedUrls.length > 0) {
        onImagesChange([...images, ...uploadedUrls]);
        
        // Limpa os estados
        setUnsplashResults([]);
        setSelectedUnsplashImages(new Set());
        setUnsplashInput('');
        
        // Muda para a aba de visualização
        setActiveTab("view");
        
        toast({
          title: "Imagens adicionadas",
          description: `${uploadedUrls.length} imagens do Unsplash foram adicionadas com sucesso.`,
          duration: 3000,
        });
      }
    } catch (error) {
      console.error('Erro ao adicionar imagens do Unsplash:', error);
      toast({
        title: "Erro ao adicionar imagens",
        description: "Ocorreu um erro ao adicionar algumas imagens do Unsplash.",
        variant: "destructive",
        duration: 3000,
      });
    } finally {
      setIsUploading(false);
    }
  };
  // Verifica se estamos no modo de visualização de imagens extraídas em tela cheia
  const isFullScreenExtraction = extractedImages.length > 0;
  const extractedImagesWithTagCount = extractedImages.filter((item) => item.tag?.trim()).length;
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        className={cn(
          "max-w-4xl overflow-hidden flex flex-col",
          isFullScreenExtraction
            ? "h-[min(90vh,900px)] max-h-[90vh]"
            : "h-auto max-h-[650px]"
        )}
      >
        <DialogHeader className="shrink-0">
          <DialogTitle>
            {isFullScreenExtraction 
              ? "Imagens Extraídas" 
              : `Gerenciar Imagens - ${contextType === 'day' ? 'Dia' : contextType === 'activity' ? 'Atividade' : contextType === 'trip' ? 'Viagem' : contextType === 'quotation' ? 'Cotação' : 'Seção'}`
            }
          </DialogTitle>
        </DialogHeader>
        {isFullScreenExtraction ? (
          <div className="flex flex-1 flex-col min-h-0 gap-3 overflow-hidden pb-2">
            <div className="shrink-0 space-y-2">
              <div className="flex flex-col gap-2 sm:flex-row sm:justify-between sm:items-start">
                <div>
                  <h3 className="font-medium">Imagens Extraídas ({extractedImages.length})</h3>
                  {extractedImagesWithTagCount > 0 ? (
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {extractedImagesWithTagCount} com identificação de quarto
                      {tagEnrichmentNote ? ' (datas alternativas)' : ''}
                    </p>
                  ) : (
                    <p className="text-xs text-amber-700 mt-0.5">
                      Nenhuma foto com tipo de quarto para estas datas no Booking.
                    </p>
                  )}
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={selectAllExtractedImages}
                  >
                    {selectedExtractedImages.size === extractedImages.length ? 'Desmarcar Todas' : 'Selecionar Todas'}
                  </Button>
                  <Button 
                    variant="default" 
                    size="sm"
                    onClick={addSelectedExtractedImages}
                    disabled={selectedExtractedImages.size === 0}
                  >
                    Adicionar Selecionadas ({selectedExtractedImages.size})
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => {
                      setSelectedExtractedImages(new Set());
                    }}
                  >
                    <Trash2 className="w-3 h-3 mr-1" />
                    Limpar Seleção
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => {
                      // Volta para o modo normal do modal, mantendo as imagens extraídas
                      setActiveTab("add");
                      setActiveInnerTab("search");
                      // Força a renderização do modo normal
                      setExtractedImages([]);
                      setTagEnrichmentNote(null);
                    }}
                  >
                    <ArrowLeft className="w-3 h-3 mr-1" />
                    Voltar
                  </Button>
                </div>
              </div>
              
              {extractionSource && (
                <div className="bg-muted p-2 rounded text-sm flex items-center gap-2">
                  <Hotel className="w-4 h-4 shrink-0" />
                  <span className="truncate">Extraído de: {extractionSource}</span>
                </div>
              )}
              {tagEnrichmentNote && (
                <p className="text-xs text-blue-800 bg-blue-50 border border-blue-100 rounded-md p-2">
                  {tagEnrichmentNote}
                </p>
              )}
              
              </div>

              <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain">
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 p-1 pb-4">
                {extractedImages.map((item, index) => {
                  const roomTag = item.tag?.trim() || null;
                  return (
                  <div
                    key={`${item.url}-${index}`}
                    className={cn(
                      "relative group aspect-square rounded-lg overflow-hidden border cursor-pointer",
                      selectedExtractedImages.has(index) && "ring-2 ring-voah-teal"
                    )}
                    onClick={() => toggleExtractedImageSelection(index)}
                  >
                    <img
                      src={item.url}
                      alt={roomTag ? `${roomTag} - Imagem ${index + 1}` : `Imagem Extraída ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 z-[1] bg-black/0 group-hover:bg-black/20 transition-all pointer-events-none" />
                    {roomTag && (
                      <div
                        className="absolute bottom-0 left-0 right-0 z-[2] bg-black/80 text-white text-[10px] leading-snug px-1.5 py-1 line-clamp-2 pointer-events-none"
                        title={roomTag}
                      >
                        {roomTag}
                      </div>
                    )}
                    {selectedExtractedImages.has(index) && (
                      <div className="absolute top-2 right-2 z-[3] pointer-events-none">
                        <div className="bg-voah-teal text-white rounded-full w-6 h-6 flex items-center justify-center shadow-md">
                          <Check className="w-4 h-4" />
                        </div>
                      </div>
                    )}
                  </div>
                  );
                })}
                </div>
              </div>
          </div>
        ) : (
          <div className="flex-1 overflow-hidden pb-4">
            <Tabs defaultValue="view" value={activeTab} onValueChange={setActiveTab} className="w-full h-full flex flex-col" style={{ minHeight: '500px' }}>
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="view">Visualizar e Organizar</TabsTrigger>
                <TabsTrigger value="add">Adicionar Novas</TabsTrigger>
              </TabsList>
              
              <div className="flex-1 overflow-hidden h-full" style={{ minHeight: '450px' }}>
                {/* Aba de Visualização e Gerenciamento */}
                <TabsContent value="view" className="space-y-2 overflow-auto h-full">
                  <div className="flex justify-between items-center">
                    <p className="text-sm text-gray-500">
                      {images.length} {images.length === 1 ? 'imagem' : 'imagens'}
                    </p>
                    <div className="flex gap-2">
                      {selectedImages.size > 0 && (
                        <Button 
                          variant="destructive" 
                          size="sm"
                          onClick={handleRemoveSelected}
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Remover Selecionadas ({selectedImages.size})
                        </Button>
                      )}
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={selectAll}
                      >
                        {selectedImages.size === images.length ? 'Desmarcar Todas' : 'Selecionar Todas'}
                      </Button>
                      {images.length > 0 && (
                        <Button 
                          variant="destructive" 
                          size="sm"
                          onClick={handleClearAll}
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Limpar Tudo
                        </Button>
                      )}
                    </div>
                  </div>
                  {images.length === 0 ? (
                    <div className="text-center py-12 space-y-4">
                      <div className="text-gray-500">Nenhuma imagem para gerenciar</div>
                      <Button onClick={() => setActiveTab("add")}>
                        <Plus className="w-4 h-4 mr-2" />
                        Adicionar Imagens
                      </Button>
                    </div>
                  ) : (
                    <>
                      <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-2 p-1 min-h-[300px]">
                        {images.map((url, index) => (
                          <div 
                            key={`${url}-${index}`} 
                            className={cn(
                              "relative group aspect-square rounded-lg overflow-hidden border cursor-pointer",
                              selectedImages.has(index) && "ring-2 ring-voah-teal"
                            )}
                            onClick={() => toggleImageSelection(index)}
                          >
                            <img
                              src={url}
                              alt={`Imagem ${index + 1}`}
                              referrerPolicy="no-referrer"
                              className="w-full h-full object-cover"
                            />
                            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all">
                              <div className="absolute top-1 right-1 flex gap-1">
                                <Button
                                  variant="destructive"
                                  size="icon"
                                  className="w-6 h-6 opacity-0 group-hover:opacity-100 transition-opacity"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleRemoveImage(index);
                                  }}
                                >
                                  <Trash2 className="w-3 h-3" />
                                </Button>
                              </div>
                              <div className="absolute bottom-1 left-1">
                                <div className="bg-black/60 text-white text-xs px-2 py-1 rounded-sm opacity-0 group-hover:opacity-100 transition-opacity">
                                  Clique para selecionar
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                      <div className="mt-2">
                        <h3 className="text-sm font-medium mb-1">Reordenar Imagens</h3>
                        <DragDropContext onDragEnd={handleDragEnd}>
                          <Droppable droppableId="images" direction="horizontal">
                            {(provided) => (
                              <div 
                                {...provided.droppableProps}
                                ref={provided.innerRef}
                                className="flex gap-1 p-1 bg-gray-50 rounded-md border min-h-[40px] overflow-x-auto"
                              >
                                {images.map((url, index) => (
                                  <Draggable 
                                    key={`drag-${url}-${index}`} 
                                    draggableId={`drag-${url}-${index}`} 
                                    index={index}
                                  >
                                    {(provided, snapshot) => (
                                      <div
                                        ref={provided.innerRef}
                                        {...provided.draggableProps}
                                        {...provided.dragHandleProps}
                                        className={cn(
                                          "w-12 h-12 relative rounded overflow-hidden border flex-shrink-0",
                                          snapshot.isDragging ? "shadow-lg ring-2 ring-voah-teal" : "",
                                          selectedImages.has(index) && "ring-2 ring-voah-teal"
                                        )}
                                      >
                                        <img
                                          src={url}
                                          alt={`Thumbnail ${index + 1}`}
                                          referrerPolicy="no-referrer"
                                          className="w-full h-full object-cover"
                                        />
                                        <div className="absolute inset-0 flex items-center justify-center bg-black/0 hover:bg-black/20">
                                          <GripVertical className="w-4 h-4 text-white opacity-0 hover:opacity-100" />
                                        </div>
                                      </div>
                                    )}
                                  </Draggable>
                                ))}
                                {provided.placeholder}
                              </div>
                            )}
                          </Droppable>
                        </DragDropContext>
                      </div>
                    </>
                  )}
                </TabsContent>
                
                {/* Aba de Adição de Imagens */}
                <TabsContent value="add" className="overflow-auto h-full">
                  <Tabs value={activeInnerTab} onValueChange={setActiveInnerTab} className="w-full h-full">
                    <TabsList className="grid w-full grid-cols-4">
                      <TabsTrigger value="url">URL da Imagem</TabsTrigger>
                      <TabsTrigger value="search">Página Inteira</TabsTrigger>
                      <TabsTrigger value="unsplash">Unsplash</TabsTrigger>
                      <TabsTrigger value="upload">Upload de Arquivos</TabsTrigger>
                    </TabsList>
                    
                    <div className="mt-4 min-h-[400px]">
                      {/* URL da Imagem */}
                      <TabsContent value="url" className="space-y-4">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                          {/* Coluna de entrada e ações */}
                          <div className="space-y-4 flex flex-col">
                            <div className="space-y-2">
                              <Label htmlFor="image-url-input">
                                URL da Imagem
                              </Label>
                              <div className="flex">
                                <Input
                                  id="image-url-input"
                                  placeholder="https://exemplo.com/imagem.jpg"
                                  onChange={handleUrlChange}
                                  value={imageUrls[0]?.url || ''}
                                  className="rounded-r-none focus:z-10"
                                  onKeyDown={(e) => e.key === 'Enter' && imageUrls[0]?.isValid && handleAddUrl()}
                                />
                                <Button 
                                  className="rounded-l-none"
                                  variant={imageUrls[0]?.isValid ? "default" : "outline"}
                                  disabled={!imageUrls[0]?.isValid}
                                  onClick={handleAddUrl}
                                >
                                  {imageUrls[0]?.isLoading ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                  ) : imageUrls[0]?.isValid ? (
                                    <>Adicionar</>
                                  ) : (
                                    <>Verificar</>
                                  )}
                                </Button>
                              </div>
                              <p className="text-xs text-gray-500 mt-1">Cole a URL direta da imagem (ex: https://site.com/imagem.jpg)</p>
                            </div>
                            {/* Status da URL */}
                            {imageUrls.length > 0 && (
                              <div className={cn(
                                "p-3 rounded-md text-sm flex items-center gap-2",
                                imageUrls[0].isLoading ? "bg-gray-100" : 
                                imageUrls[0].isValid ? "bg-green-50 text-green-700 border border-green-200" : 
                                "bg-red-50 text-red-700 border border-red-200"
                              )}>
                                {imageUrls[0].isLoading ? (
                                  <>
                                    <Loader2 className="w-4 h-4 animate-spin flex-shrink-0" />
                                    <span>Verificando URL...</span>
                                  </>
                                ) : imageUrls[0].isValid ? (
                                  <>
                                    <CheckCircle2 className="w-4 h-4 flex-shrink-0" />
                                    <span>URL válida e pronta para ser adicionada</span>
                                  </>
                                ) : (
                                  <>
                                    <AlertCircle className="w-4 h-4 flex-shrink-0" />
                                    <span>{imageUrls[0].error || 'URL inválida'}</span>
                                  </>
                                )}
                              </div>
                            )}
                            {/* Instruções */}
                            {!imageUrls.length && (
                              <div className="bg-gray-50 p-4 rounded-md space-y-2 border border-gray-200">
                                <h4 className="text-sm font-medium flex items-center gap-2">
                                  <LinkIcon className="w-4 h-4 text-gray-500" />
                                  Como adicionar uma imagem por URL
                                </h4>
                                <ul className="text-xs text-gray-600 space-y-1 list-disc pl-5">
                                  <li>Cole a URL direta da imagem (terminando com .jpg, .png, etc.)</li>
                                  <li>A URL deve ser acessível publicamente</li>
                                  <li>O sistema verificará automaticamente se a URL é válida</li>
                                  <li>Clique em "Adicionar" quando a URL for validada</li>
                                  <li>Pressione Enter para adicionar rapidamente quando a URL for válida</li>
                                </ul>
                              </div>
                            )}
                            
                            {/* Botões de ação para telas pequenas */}
                            {imageUrls.length > 0 && imageUrls[0].isValid && (
                              <div className="flex justify-end gap-2 lg:hidden mt-4">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={handleCancelUrl}
                                >
                                  Cancelar
                                </Button>
                                <Button
                                  size="sm"
                                  onClick={handleAddUrl}
                                  disabled={!imageUrls[0]?.isValid || isUploading}
                                >
                                  {isUploading ? (
                                    <>
                                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                      Enviando...
                                    </>
                                  ) : (
                                    <>Adicionar à Galeria</>
                                  )}
                                </Button>
                              </div>
                            )}
                          </div>
                          {/* Coluna de preview */}
                          <div className="space-y-3 flex flex-col justify-start">
                            {imageUrls.length > 0 && imageUrls[0].isValid ? (
                              <>
                                <div className="font-medium text-sm flex items-center gap-2 text-gray-700">
                                  <Camera className="w-4 h-4 text-gray-500" />
                                  Preview da Imagem
                                </div>
                                <div className="border rounded-md overflow-hidden bg-gray-50 aspect-video flex items-center justify-center p-2">
                                  <img
                                    src={imageUrls[0].url}
                                    alt="Preview da imagem"
                                    referrerPolicy="no-referrer"
                                    className="max-w-full max-h-full object-contain shadow-sm"
                                  />
                                </div>
                                {/* Botões de ação para telas grandes */}
                                <div className="hidden lg:flex justify-end gap-2">
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={handleCancelUrl}
                                  >
                                    Cancelar
                                  </Button>
                                  <Button
                                    size="sm"
                                    onClick={handleAddUrl}
                                    disabled={!imageUrls[0]?.isValid || isUploading}
                                  >
                                    {isUploading ? (
                                      <>
                                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                        Enviando...
                                      </>
                                    ) : (
                                      <>Adicionar à Galeria</>
                                    )}
                                  </Button>
                                </div>
                              </>
                            ) : (
                              <div className="border rounded-md bg-gray-50 flex flex-col items-center justify-center text-gray-400 p-4 mt-7" style={{ minHeight: '250px' }}>
                                <ImagePlus className="w-12 h-12 mb-2 opacity-20" />
                                <p className="text-sm text-center">O preview da imagem aparecerá aqui quando uma URL válida for inserida</p>
                              </div>
                            )}
                          </div>
                        </div>
                      </TabsContent>
                      
                      {/* Página Inteira (antigo Buscar Imagens) */}
                      <TabsContent value="search" className="space-y-4">
                        <div className="space-y-2">
                          <Label>Buscar imagens ou extrair de URL</Label>
                          <div className="flex gap-2">
                            <Input
                              placeholder="Digite uma busca ou cole uma URL"
                              value={searchInput}
                              onChange={handleSearchInputChange}
                              onKeyDown={(e) => e.key === 'Enter' && !isValidUrl(searchInput) && handleGoogleSearch('general')}
                            />
                          </div>
                        </div>
                        {/* Botões condicionais baseados no tipo de input */}
                        <div className="flex gap-2">
                          {isValidUrl(searchInput) ? (
                            // Quando uma URL é detectada automaticamente, usa EXTENSÃO (fluxo antigo)
                            <Button
                              onClick={() => extractFromUrlWithExtension(searchInput)}
                              disabled={isExtracting}
                              className="w-full"
                            >
                              {isExtracting ? (
                                <>
                                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                  Extraindo...
                                </>
                              ) : (
                                <>
                                  <Hotel className="w-4 h-4 mr-2" />
                                  Extrair Imagens
                                </>
                              )}
                            </Button>
                          ) : (
                            <>
                              <Button
                                onClick={() => handleGoogleSearch('booking')}
                                disabled={isSearching || !searchInput.trim()}
                                className="flex-1"
                              >
                                {isSearching ? (
                                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                ) : (
                                  <Hotel className="w-4 h-4 mr-2" />
                                )}
                                Booking.com
                              </Button>
                              <Button
                                onClick={() => handleGoogleSearch('general')}
                                disabled={isSearching || !searchInput.trim()}
                                className="flex-1"
                              >
                                {isSearching ? (
                                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                ) : (
                                  <Globe className="w-4 h-4 mr-2" />
                                )}
                                Web
                              </Button>
                            </>
                          )}
                        </div>
                        {/* Indicador de carregamento durante a busca */}
                        {isSearching && (
                          <div className="flex items-center justify-center p-8">
                            <Loader2 className="w-8 h-8 animate-spin text-voah-teal" />
                            <span className="ml-2">Buscando imagens...</span>
                          </div>
                        )}
                        {/* Resultados da busca do Google */}
                        {!isSearching && searchResults.length > 0 && (
                          <div className="space-y-4">
                            <div className="flex justify-between items-center">
                              <h3 className="font-medium">Resultados da Busca ({searchResults.length})</h3>
                            </div>
                            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 max-h-[40vh] overflow-y-auto p-1">
                              {searchResults.map((result, index) => (
                                <div
                                  key={`result-${index}`}
                                  className="relative group aspect-square rounded-lg overflow-hidden border cursor-pointer hover:bg-gray-50"
                                  onClick={() => handleSelectSearchResult(result)}
                                >
                                  <img
                                    src={result.link}
                                    alt={result.title || 'Resultado da busca'}
                                    className="w-full h-full object-cover"
                                  />
                                  <div className="absolute inset-0 bg-black bg-opacity-0 hover:bg-opacity-30 transition-all duration-200 flex items-end">
                                    <div className="p-2 w-full bg-black bg-opacity-60 text-white text-xs truncate">
                                      {result.title || 'Sem título'}
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        {/* Mensagem quando não há resultados */}
                        {!isSearching && searchResults.length === 0 && !isFullScreenExtraction && searchInput && !isValidUrl(searchInput) && (
                          <div className="text-center text-gray-500 p-4">
                            Digite um termo e clique em um dos botões de busca acima
                          </div>
                        )}
                        {/* Nota: Os resultados da extração agora são exibidos em tela cheia */}
                      </TabsContent>
                      
                      {/* Unsplash */}
                      <TabsContent value="unsplash" className="space-y-4">
                        <div className="space-y-2">
                          <Label>Buscar imagens no Unsplash</Label>
                          <div className="flex gap-2">
                            <Input
                              placeholder="Digite o que deseja buscar no Unsplash"
                              value={unsplashInput}
                              onChange={handleUnsplashInputChange}
                              onKeyDown={(e) => e.key === 'Enter' && unsplashInput.trim() && handleUnsplashSearch()}
                            />
                            <Button
                              onClick={handleUnsplashSearch}
                              disabled={isUnsplashSearching || !unsplashInput.trim()}
                            >
                              {isUnsplashSearching ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <Search className="w-4 h-4" />
                              )}
                            </Button>
                          </div>
                        </div>
                        
                        {/* Resultados da busca no Unsplash */}
                        {unsplashResults.length > 0 && (
                          <div className="space-y-4">
                            <div className="flex justify-between items-center">
                              <h3 className="font-medium">Resultados ({unsplashResults.length})</h3>
                              <div className="flex gap-2">
                                <Button 
                                  variant="outline" 
                                  size="sm"
                                  onClick={selectAllUnsplashImages}
                                >
                                  {selectedUnsplashImages.size === unsplashResults.length ? 'Desmarcar Todas' : 'Selecionar Todas'}
                                </Button>
                                <Button 
                                  variant="default" 
                                  size="sm"
                                  onClick={addSelectedUnsplashImages}
                                  disabled={selectedUnsplashImages.size === 0 || isUploading}
                                >
                                  {isUploading ? (
                                    <>
                                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                      Enviando...
                                    </>
                                  ) : (
                                    <>Adicionar Selecionadas ({selectedUnsplashImages.size})</>
                                  )}
                                </Button>
                              </div>
                            </div>
                            
                            <div className="text-xs text-gray-500 mb-2">
                              <span>Imagens fornecidas por </span>
                              <a href="https://unsplash.com/?utm_source=travel_magic&utm_medium=referral" 
                                 target="_blank" 
                                 rel="noopener noreferrer"
                                 className="text-black font-medium">
                                Unsplash
                              </a>
                            </div>
                            
                            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 max-h-[40vh] overflow-y-auto p-1">
                              {unsplashResults.map((photo, index) => (
                                <div
                                  key={photo.id}
                                  className={cn(
                                    "relative group aspect-square rounded-lg overflow-hidden border cursor-pointer",
                                    selectedUnsplashImages.has(index) && "ring-2 ring-voah-teal"
                                  )}
                                  onClick={() => toggleUnsplashImageSelection(index)}
                                >
                                  <img
                                    src={photo.urls.small}
                                    alt={photo.description || 'Imagem do Unsplash'}
                                    className="w-full h-full object-cover"
                                  />
                                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all">
                                    {selectedUnsplashImages.has(index) && (
                                      <div className="absolute top-2 right-2">
                                        <div className="bg-voah-teal text-white rounded-full w-6 h-6 flex items-center justify-center">
                                          <Check className="w-4 h-4" />
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                  <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-xs p-1 truncate">
                                    {photo.user.name}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </TabsContent>
                      
                      {/* Upload de Arquivos */}
                      <TabsContent value="upload" className="space-y-4">
                        <div className="space-y-2">
                          <Label>Upload de Arquivos</Label>
                          
                          {/* Container de Drop Zone */}
                          <div 
                            className={cn(
                              "border-2 border-dashed rounded-lg p-8 transition-colors text-center cursor-pointer min-h-[200px] flex flex-col items-center justify-center",
                              isDragging ? "border-voah-teal bg-voah-teal/10" : "border-gray-300 hover:border-voah-teal"
                            )}
                            onDragOver={handleDragOver}
                            onDragLeave={handleDragLeave}
                            onDrop={handleDrop}
                            onClick={() => fileInputRef.current?.click()}
                          >
                            <Upload className="w-10 h-10 text-gray-400 mb-2" />
                            <p className="text-sm text-gray-500 mb-1">
                              Arraste e solte arquivos aqui ou clique para selecionar
                            </p>
                            <p className="text-xs text-gray-400">
                              Formatos suportados: JPG, PNG, GIF, WEBP (máx. 5MB)
                            </p>
                            
                            <input
                              type="file"
                              multiple
                              onChange={handleFileSelect}
                              ref={fileInputRef}
                              className="hidden"
                              accept="image/*"
                            />
                          </div>
                        </div>
                        {/* Botões de ação */}
                        <div className="flex justify-end gap-2">
                          <Button
                            onClick={handleSubmitFiles}
                            disabled={isUploading || files.length === 0}
                          >
                            {isUploading ? (
                              <>
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                Enviando...
                              </>
                            ) : (
                              <>
                                <Upload className="w-4 h-4 mr-2" />
                                Adicionar {files.length > 0 ? `(${files.length})` : ''}
                              </>
                            )}
                          </Button>
                        </div>
                        {/* Preview dos arquivos */}
                        {files.length > 0 && (
                          <div className="mt-4 space-y-2">
                            <h3 className="text-sm font-medium">Arquivos para upload</h3>
                            <div className="space-y-2">
                              {files.map((file, index) => (
                                <div key={index} className="flex items-center space-x-2 p-2 border rounded">
                                  <div className="flex-1 flex items-center">
                                    <span className="text-sm truncate">{file.name}</span>
                                  </div>
                                  <div className="flex items-center">
                                    {uploadProgress[file.name] ? (
                                      uploadProgress[file.name] === 100 ? (
                                        <CheckCircle2 className="h-4 w-4 text-green-500" />
                                      ) : (
                                        <div className="h-4 w-4 text-blue-500 relative">
                                          <div className="absolute inset-0 flex items-center justify-center">
                                            <span className="text-[8px]">{uploadProgress[file.name]}%</span>
                                          </div>
                                        </div>
                                      )
                                    ) : errors[file.name] ? (
                                      <AlertCircle className="h-4 w-4 text-red-500" />
                                    ) : (
                                      <div className="h-4 w-4" />
                                    )}
                                    <Button 
                                      type="button" 
                                      variant="ghost" 
                                      size="sm"
                                      onClick={() => {
                                        setFiles(prev => prev.filter((_, i) => i !== index));
                                        const newErrors = { ...errors };
                                        delete newErrors[file.name];
                                        setErrors(newErrors);
                                      }}
                                      className="h-6 w-6 p-0 ml-1"
                                    >
                                      <Trash2 className="h-4 w-4 text-red-500" />
                                    </Button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </TabsContent>
                    </div>
                  </Tabs>
                </TabsContent>
              </div>
            </Tabs>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}